import React, { useState } from 'react';
import { motion } from 'framer-motion';

const Volunteer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skills: '',
    availability: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Volunteer form submitted:', formData);
    // Handle form submission
  };

  const volunteerOpportunities = [
    {
      title: "Program Coordination",
      description: "Help coordinate and manage our development programs in remote villages.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
        </svg>
      )
    },
    {
      title: "Medical Support",
      description: "Provide healthcare services and medical expertise to underserved communities.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
        </svg>
      )
    },
    {
      title: "Education & Training",
      description: "Share knowledge and skills through educational programs and workshops.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
        </svg>
      )
    },
    {
      title: "Fundraising",
      description: "Help raise funds and awareness for our mission through various initiatives.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
        </svg>
      )
    }
  ];

  return (
    <section className="relative py-24 bg-gradient-to-br from-black via-neutral-900 to-neutral-800 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-dot-white opacity-10"></div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-gradient">
              Volunteer With Us
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-yellow-600 mx-auto rounded-full mb-8"></div>
          <p className="text-xl text-neutral-300 max-w-3xl mx-auto">
            Join our mission to transform lives in Nepal's remote villages. Your skills and passion can make a real difference.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Volunteer Opportunities */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="glass rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Volunteer Opportunities</h3>
              <div className="space-y-6">
                {volunteerOpportunities.map((opportunity, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-4 p-4 rounded-xl hover:bg-neutral-800/50 transition-colors duration-300"
                  >
                    <div className="text-amber-400 mt-1">
                      {opportunity.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">{opportunity.title}</h4>
                      <p className="text-neutral-300 text-sm">{opportunity.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Quote */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="glass rounded-2xl p-8"
            >
              <div className="text-amber-400 mb-4">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"></path>
                </svg>
              </div>
              <blockquote className="text-lg text-neutral-300 italic mb-4">
                "We have supporters from all over the world. People who believe in our mission and want to help create lasting change in Nepal's most remote villages."
              </blockquote>
              <cite className="text-amber-400 font-semibold">— Arati Basnet, Co-Founder</cite>
            </motion.div>
          </motion.div>

          {/* Volunteer Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="glass rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-8 text-center">Join Our Team</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-neutral-300 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full p-3 rounded-lg bg-neutral-800 border border-neutral-600 text-white placeholder-neutral-400 focus:border-amber-500 focus:outline-none transition-colors duration-300"
                      placeholder="Your name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-neutral-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-3 rounded-lg bg-neutral-800 border border-neutral-600 text-white placeholder-neutral-400 focus:border-amber-500 focus:outline-none transition-colors duration-300"
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full p-3 rounded-lg bg-neutral-800 border border-neutral-600 text-white placeholder-neutral-400 focus:border-amber-500 focus:outline-none transition-colors duration-300"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-300 mb-2">
                    Skills & Expertise
                  </label>
                  <input
                    type="text"
                    name="skills"
                    value={formData.skills}
                    onChange={handleChange}
                    className="w-full p-3 rounded-lg bg-neutral-800 border border-neutral-600 text-white placeholder-neutral-400 focus:border-amber-500 focus:outline-none transition-colors duration-300"
                    placeholder="Healthcare, Education, Project Management, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-300 mb-2">
                    Availability
                  </label>
                  <select
                    name="availability"
                    value={formData.availability}
                    onChange={handleChange}
                    className="w-full p-3 rounded-lg bg-neutral-800 border border-neutral-600 text-white focus:border-amber-500 focus:outline-none transition-colors duration-300"
                  >
                    <option value="">Select your availability</option>
                    <option value="part-time">Part-time (few hours/week)</option>
                    <option value="weekends">Weekends only</option>
                    <option value="full-time">Full-time commitment</option>
                    <option value="project-based">Project-based</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-300 mb-2">
                    Why do you want to volunteer?
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full p-3 rounded-lg bg-neutral-800 border border-neutral-600 text-white placeholder-neutral-400 focus:border-amber-500 focus:outline-none transition-colors duration-300 resize-none"
                    placeholder="Tell us about your motivation and how you'd like to help..."
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full relative px-8 py-4 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-semibold rounded-lg btn-shine transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-amber-500/25"
                >
                  <span className="relative z-10">Submit Application</span>
                </button>
              </form>
            </div>

            {/* Glow effect */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-amber-600/20 to-yellow-600/20 blur-xl opacity-0 hover:opacity-100 transition-opacity duration-500 -z-10"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Volunteer; 